import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { subscriptionApi } from "../services/userApi";

const SubscriptionContext = createContext(null);

/*
 * The FOURTH stale copy of the tier vocabulary, and the one that survived
 * b585a19's sweep of the other three.
 *
 * This still named Standard and AI -- tiers V047 (#234) removed -- and had
 * no entry for either tier that actually exists. So a paying account's
 * badge looked up TIER_LABELS["Business"], got undefined, and rendered an
 * empty badge; only Free, which by luck maps to itself, ever looked right.
 *
 * No map any more. The API's tier names ARE the display names -- "Free",
 * "Harvester", "Business" are words for people, not internal codes -- so
 * there is nothing left to keep in sync. A map that only ever restated its
 * input was the whole defect.
 */

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      setStatus(await subscriptionApi.get());
    } catch {
      /*
       * SWALLOWED, AND status STAYS NULL (#412).
       *
       * There was no catch at all, so a failed lookup rejected into
       * nothing -- an unhandled promise rejection in the browser, and
       * silence here. The `finally` still cleared `loading`, so the app
       * carried on as though the answer had arrived.
       *
       * Null is the right state: it means "we do not know", which the
       * badge now renders as nothing rather than as the cheapest plan.
       * Nothing else reads the tier to decide what somebody may DO --
       * the features come from their own endpoint and the server
       * enforces them regardless -- so an unknown tier costs a badge,
       * not access.
       */
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      status,
      loading,
      refresh,
      /*
       * NULL WHEN WE DO NOT KNOW, never "Free" (#412).
       *
       * The comment that used to sit here said exactly the right thing --
       * "labelling an unknown tier as the cheapest one is how a Business
       * customer gets told they are on the free plan" -- above code that
       * did precisely that: `status?.tier || "Free"`.
       *
       * status is null before the call resolves AND after it fails, and
       * refresh() has no catch, so a failed subscription lookup left a
       * paying account labelled Free for the rest of the session. Found
       * from a device: every paid page rendered correctly -- the features
       * come from a different endpoint and were right -- while the badge
       * said Free, which reads as the features being over-granted rather
       * than the badge being wrong.
       *
       * The badge renders nothing rather than guessing. Not knowing is a
       * state, and saying nothing is the honest way to show it.
       */
      tierLabel: status?.tier ?? null,
      // Unchanged in meaning and now consistent with the label: unknown
      // is not free. It was already `=== "Free"`, which is why a null
      // status produced a neutral badge reading "Free" -- a combination
      // no real tier can produce, and the clue that this was a fallback
      // nobody intended.
      isFree: status?.tier === "Free",
      atHiveLimit: Boolean(status?.limit_reached),
      hiveLimit: status?.hive_limit ?? null,
      activeHiveCount: status?.active_hive_count ?? 0,
    }),
    [status, loading, refresh]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}
